import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { hostname, isSafeUrl } from "../../../utils";
import SyncFrequencySelect from "../components/SyncFrequencySelect";

interface GoogleDocsIntegrationFormProps {
  getToken: () => Promise<string>;
  editMode?: boolean;
  initialData?: {
    folderId?: string;
    syncFrequency?: string;
    composioConnectedAccountId?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: {
    folderId: string;
    syncFrequency: string;
    composioConnectedAccountId?: string;
  }) => void;
}

export default function GoogleDocsIntegrationForm({
  getToken,
  editMode = false,
  initialData,
  onValidationChange,
  onDataChange,
}: GoogleDocsIntegrationFormProps) {
  const [folderId, setFolderId] = useState(initialData?.folderId || "root");
  const [syncFrequency, setSyncFrequency] = useState(
    initialData?.syncFrequency || "manual"
  );

  // Composio OAuth state
  const [composioConnectedAccountId, setComposioConnectedAccountId] = useState(
    initialData?.composioConnectedAccountId || ""
  );
  const [composioConnecting, setComposioConnecting] = useState(false);
  const [composioConnected, setComposioConnected] = useState(
    !!initialData?.composioConnectedAccountId
  );
  const [composioPollingInterval, setComposioPollingInterval] =
    useState<NodeJS.Timeout | null>(null);

  // Folder list state
  const [folderState, setFolderState] = useState<{
    isLoading: boolean;
    folders: Array<{ id: string; name: string; path: string; level: number }>;
    error: string | null;
  }>({
    isLoading: false,
    folders: [],
    error: null,
  });

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (composioPollingInterval) {
        clearInterval(composioPollingInterval);
      }
    };
  }, [composioPollingInterval]);

  // Load folders when connected
  useEffect(() => {
    if (composioConnected && composioConnectedAccountId) {
      loadFolders();
    } else {
      setFolderState({ isLoading: false, folders: [], error: null });
    }
  }, [composioConnected, composioConnectedAccountId]);

  // Notify parent of validation status
  useEffect(() => {
    const isFormValid = composioConnected && !folderState.isLoading;
    onValidationChange(isFormValid);
  }, [composioConnected, folderState, onValidationChange]);

  // Notify parent of data changes
  useEffect(() => {
    onDataChange({
      folderId,
      syncFrequency,
      composioConnectedAccountId: composioConnectedAccountId || undefined,
    });
  }, [folderId, syncFrequency, composioConnectedAccountId, onDataChange]);

  const loadFolders = async () => {
    if (!composioConnectedAccountId) return;

    setFolderState({ isLoading: true, folders: [], error: null });

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/integrations/google_docs/composio/list_folders`,
        {
          params: { connectedAccountId: composioConnectedAccountId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.error) {
        setFolderState({
          isLoading: false,
          folders: [],
          error: response.data.error,
        });
      } else {
        setFolderState({
          isLoading: false,
          folders: response.data.folders || [],
          error: null,
        });

        if (!folderId) {
          setFolderId("root");
        }
      }
    } catch (error: any) {
      setFolderState({
        isLoading: false,
        folders: [],
        error: error.response?.data?.error || "Failed to load folders",
      });
    }
  };

  // Composio OAuth flow
  const startComposioOAuth = async () => {
    setComposioConnecting(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/integrations/composio/connect`,
        {
          integrationType: "google_docs",
          companyId: "temp",
          instanceName: "Google Drive",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { redirectUrl } = response.data;

      // Open OAuth popup
      const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
      if (!safeUrlPattern.test(redirectUrl)) return;
      const popup = window.open(
        redirectUrl,
        "Google Drive OAuth",
        "width=600,height=700,scrollbars=yes"
      );

      // Poll for connection status
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await axios.get(
            `${hostname}/integrations/composio/status`,
            {
              params: {
                integrationType: "google_docs",
                companyId: "temp",
              },
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (statusResponse.data.connected) {
            clearInterval(pollInterval);
            setComposioPollingInterval(null);
            setComposioConnecting(false);

            // Get the connected account ID
            const connections = statusResponse.data.connections || [];
            const activeConnection = connections.find(
              (c: any) => c.status === "ACTIVE"
            );
            if (activeConnection) {
              setComposioConnectedAccountId(activeConnection.id);
              setComposioConnected(true);
            }

            // Close popup if still open
            if (popup && !popup.closed) {
              popup.close();
            }
          }
        } catch (err) {
          console.error("Error checking connection status:", err);
        }
      }, 2000);

      setComposioPollingInterval(pollInterval);

      // Stop polling after 5 minutes
      setTimeout(() => {
        if (pollInterval) {
          clearInterval(pollInterval);
          setComposioPollingInterval(null);
          setComposioConnecting(false);
        }
      }, 5 * 60 * 1000);
    } catch (error: any) {
      console.error("Failed to start OAuth:", error);
      setComposioConnecting(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {/* Composio OAuth Section */}
      <Box sx={{ mb: 2 }}>
        {composioConnected ? (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Google Drive Connected
            </Typography>
            <Typography variant="body2">
              Your Google account is connected. Select a folder to sync below.
            </Typography>
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                Connect your Google Drive
              </Typography>
              <Typography variant="body2">
                Click the button below to securely connect your Google account.
                You'll be redirected to Google to authorize access.
              </Typography>
            </Alert>
            <Button
              variant="contained"
              onClick={startComposioOAuth}
              disabled={composioConnecting}
              startIcon={
                composioConnecting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{ mb: 2 }}
            >
              {composioConnecting ? "Connecting..." : "Connect Google Drive"}
            </Button>
          </>
        )}
      </Box>

      {/* Folder Selection - Show when connected */}
      {composioConnected && (
        <>
          {folderState.isLoading && (
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Loading available folders...
              </Typography>
            </Box>
          )}

          {folderState.error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={loadFolders}
                  title="Retry loading folders"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              }
            >
              {folderState.error}
            </Alert>
          )}

          {!folderState.isLoading && folderState.folders.length > 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Select a folder to sync.
                </Typography>
                <IconButton
                  onClick={loadFolders}
                  size="small"
                  title="Refresh folder list"
                  disabled={folderState.isLoading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
              <FormControl fullWidth>
                <InputLabel>Select Folder to Sync</InputLabel>
                <Select
                  value={folderId || "root"}
                  label="Select Folder to Sync"
                  onChange={(e) => setFolderId(e.target.value)}
                >
                  {folderState.folders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: folder.level === 0 ? "bold" : "normal",
                            pl: folder.level * 2,
                          }}
                        >
                          {folder.name}
                        </Typography>
                        {folder.level > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ pl: folder.level * 2 }}
                          >
                            {folder.path}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {!folderState.isLoading &&
            folderState.folders.length === 0 &&
            !folderState.error && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={loadFolders}
                    title="Refresh folder list"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                }
              >
                No folders found. Click refresh to try again.
              </Alert>
            )}
        </>
      )}

      {/* Auto-Sync Frequency */}
      <SyncFrequencySelect
        value={syncFrequency}
        onChange={setSyncFrequency}
        sx={{ mt: 2 }}
      />
    </Box>
  );
}
